import { useCallback, useId, useState, type ChangeEvent } from 'react';
import {
  Button,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { PopupTextInputProps } from './PopupTextInput.types';

export type { PopupTextInputProps };

const PopupTextInput = (props: PopupTextInputProps) => {
  const { t } = useTranslation('common');

  const {
    description,
    label,
    defaultText,
    placeholder,
    isMandatory,
    disabled,
    maxLength,
    onChange,
  } = props;

  const [open, setOpen] = useState(false);
  const [textContent, setTextContent] = useState<string>(defaultText ?? '');
  const inputId = useId();

  const showError = isMandatory === true && textContent === '';

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(event.target.value);
      setTextContent(event.target.value);
    },
    [onChange],
  );

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        type="button"
        disabled={disabled}
        variant="outline"
        aria-invalid={showError && open ? true : undefined}
        className="min-w-auto whitespace-nowrap"
      >
        {label} {textContent !== '' && <CheckIcon className="size-4" />}
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {description === undefined || description === '' ? (
        trigger
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{trigger}</TooltipTrigger>
            <TooltipContent side="top">{description}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <PopoverContent align="start" side="top" className="pointer-events-auto w-80 p-2">
        <div className="mt-2 mb-3 flex w-full items-center justify-between gap-x-2 px-1">
          <Label htmlFor={inputId} className="text-base font-normal text-muted-foreground">
            {label ?? t('components.popupTextInput.select')}
          </Label>
          <Button type="button" onClick={() => setOpen(false)} variant="outline" size="sm">
            {t('components.popupTextInput.done')}
          </Button>
        </div>
        {showError && (
          <p className="mt-1 text-xs text-destructive">
            {t('components.popupTextInput.atLeastOneSelectionNeeded')}
          </p>
        )}
        <Textarea
          id={inputId}
          value={textContent}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          maxLength={maxLength}
          aria-invalid={showError ? true : undefined}
          className="h-32 w-full resize-none overflow-y-auto bg-muted"
        />
      </PopoverContent>
    </Popover>
  );
};

export default PopupTextInput;
